import { SITE_URL } from "@shared/env";
import { UrlInjectionMap } from "../types";

export default class Url {
  /**
   * Map path(s) to a full URL, including origin.
   *
   * @example format("api/{my?}/{variable}/endpoint", { variable: "value" }) -> "https://example.com/api/value/endpoint/"
   */
  public static format(
    path: string | Array<string | undefined> = "",
    injects?: UrlInjectionMap,
    opts: {
      relative?: boolean;
      origin?: string;
    } = {
      relative: false,
      origin: SITE_URL,
    }
  ) {
    const url = Url.formatEndpointUrl(path, injects);

    // Return a relative URL if no origin is provided
    if (!opts.origin || opts.relative) {
      return url;
    }

    try {
      return new URL(url, opts.origin).toString();
    } catch (e) {
      // If we throw here we're probably looking at a malformed URL
      // so try to fall back to a relative url.
      return url;
    }
  }

  /**
   * Consistently formats endpoint URLs and allow for variable injection.
   * Not for use with protocols (ie, https://)
   *
   * @example formatEndpointUrl("api/{my?}/{variable}/endpoint", { variable: "value" }) -> "/api/value/endpoint/"
   */
  public static formatEndpointUrl(
    paths: string | Array<string | undefined> = "",
    inject?: UrlInjectionMap,
    shouldValidateUrl?: boolean
  ): string {
    let output = (Array.isArray(paths) ? paths : [paths])
      .filter((path) => path && path !== "/")
      .join("/");

    // Search for variables within curly brackets, extract, and inject their values
    output.match(/{([^}]+)}/g)?.forEach((param) => {
      // param is optional if it ends with ?} or if we're not validating the URL
      const isOptional = param.trim().endsWith("?}") || !shouldValidateUrl;
      const name = param.replace(/{|}|\?/g, "");

      // If not optional and not provided, log an error - logging is preferable to crashing the page
      if (!isOptional && !inject?.[name]) {
        // eslint-disable-next-line no-console
        console.error(`Missing required route parameter: ${name}`);
      }

      // Replace the part of the string where the param was with the value
      output = output.replace(param, String(inject?.[name] ?? ""));
    });

    // strip deep links
    const [path, hash] = output.split("#");
    if (hash) {
      output = path;
    }

    // strip query params
    const [pathname, search] = output.split("?");
    if (search) {
      output = pathname;
    }

    // force leading slash
    if (!output.startsWith("/")) {
      output = `/${output}`;
    }

    // If last path has a file extension, remove trailing slash
    const lastPath = output.replace(/\/$/, "").split("/").pop();
    if (lastPath?.includes(".")) {
      output = output.replace(/\/$/, "");
    }

    // force trailing slash if not a file path
    if (!output.endsWith("/") && !output.includes(".")) {
      output = `${output}/`;
    }

    // remove places where there are two slashes in a row except where it might be a protocol
    if (output.includes("//")) {
      output = output.replace(/\/\//g, "/").replace(/:\//g, "://");
    }

    if (search) {
      output = `${output}?${search}`;
    }

    if (hash) {
      output = `${output}#${hash}`;
    }

    return output;
  }
}
